import {
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from "@heroicons/react/20/solid";
import { type ComponentType, type SVGProps } from "react";
import { Button } from "./catalyst/button";
import { Link } from "./catalyst/link";
import type React from "react";

export type AlertVariant = "info" | "warning" | "error";

export const variantStyles: Record<
  AlertVariant,
  {
    container: string;
    icon: string;
    text: string;
  }
> = {
  info: {
    container:
      "rounded-md bg-blue-50 p-4 dark:bg-blue-500/10 dark:outline dark:outline-blue-500/20",
    icon: "text-blue-400",
    text: "text-blue-700 dark:text-blue-300",
  },
  warning: {
    container:
      "rounded-md bg-amber-50 p-4 dark:bg-amber-500/10 dark:outline dark:outline-amber-500/20",
    icon: "text-amber-500",
    text: "text-amber-800 dark:text-amber-200",
  },
  error: {
    container:
      "rounded-md bg-red-50 p-4 dark:bg-red-500/10 dark:outline dark:outline-red-500/20",
    icon: "text-red-500",
    text: "text-red-800 dark:text-red-200",
  },
};

const defaultIcons: Record<
  AlertVariant,
  ComponentType<SVGProps<SVGSVGElement>>
> = {
  info: InformationCircleIcon,
  warning: ExclamationTriangleIcon,
  error: ExclamationCircleIcon,
};

export type InfoAlertProps = {
  /**
   * The variant/type of alert (info or warning)
   * @default "info"
   */
  variant?: AlertVariant;
  /**
   * The message content to display. Can be a string or ReactNode for flexibility.
   */
  message: React.ReactNode;
  /**
   * The action to display on the right side. Can be a Link, Button, or any ReactNode.
   */
  action?: React.ReactNode;
  /**
   * The action to perform when the action is clicked.
   */
  onAction?: () => void;
  /**
   * The href to navigate to when the action is clicked.
   */
  href?: React.ComponentProps<typeof Link>["href"];
  /**
   * Optional custom icon component. If not provided, uses default icon based on variant.
   */
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
};

export function InfoAlert({
  variant = "info",
  message,
  icon,
  action,
  onAction,
  href,
}: InfoAlertProps) {
  const styles = variantStyles[variant];
  const Icon = icon ?? defaultIcons[variant];

  return (
    <div className={styles.container}>
      <div className="flex items-start md:items-center">
        <div className="shrink-0">
          <Icon aria-hidden="true" className={`size-5 ${styles.icon}`} />
        </div>
        <div className="ml-3 flex-1 flex flex-col md:flex-row md:justify-between items-center">
          <p className={`text-sm ${styles.text}`}>{message}</p>
          {action && (
            <Button onClick={onAction} href={href ?? "#"} plain>
              {action}
              <span aria-hidden="true"> &rarr;</span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
